import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';

export const getAdornisFilesByIDs = registerQuery({
  operationName: 'getAdornisFilesByIDs',
  type: () => [AdornisFile],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<AdornisFile>>(gqlFields: SelSet) =>
      readEntitiesByIDs(AdornisFile, ids, gqlFields),
});

export const subscribeAdornisFilesByIDs = registerSubscription({
  operationName: 'subscribeAdornisFilesByIDs',
  type: () => [AdornisFile],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<AdornisFile>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(AdornisFile, ids, gqlFields),
});

export const getAllAdornisFiles = registerQuery({
  operationName: 'getAllAdornisFiles',
  type: () => [AdornisFile],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<AdornisFile>>(gqlFields: SelSet) =>
      readAllEntities(AdornisFile, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllAdornisFiles = registerSubscription({
  operationName: 'subscribeAllAdornisFiles',
  type: () => [AdornisFile],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<AdornisFile>>(gqlFields: SelSet) =>
      watchAllEntities(AdornisFile, gqlFields, { skip, limit, excludeIDs }),
});

export const getAdornisFileByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<AdornisFile>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getAdornisFilesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeAdornisFileByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<AdornisFile>>(gqlFields: SelSet) =>
    id ? subscribeAdornisFilesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
